import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Boxes, Trash2, Minus, AlertTriangle, Loader2 } from "lucide-react";

export default function Inventory() {
  const [properties, setProperties] = useState([]);
  const [selected, setSelected] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", quantity: 0, threshold: 1 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProperties(r.data);
      if (r.data[0]) setSelected(r.data[0].id);
    });
  }, []);

  const load = (pid) => {
    if (!pid) return;
    api.get(`/properties/${pid}/inventory`).then((r) => setItems(r.data));
  };
  useEffect(() => {
    load(selected);
  }, [selected]);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Item name required");
    setSaving(true);
    try {
      await api.post(`/properties/${selected}/inventory`, {
        name: form.name,
        quantity: parseInt(form.quantity) || 0,
        threshold: parseInt(form.threshold) || 1,
      });
      toast.success("Item added");
      setOpen(false);
      setForm({ name: "", quantity: 0, threshold: 1 });
      load(selected);
    } catch (e) {
      toast.error("Could not add item");
    } finally {
      setSaving(false);
    }
  };

  const updateQty = async (item, delta) => {
    const quantity = Math.max(0, item.quantity + delta);
    await api.put(`/inventory/${item.id}`, { quantity });
    load(selected);
  };

  const remove = async (id) => {
    await api.delete(`/inventory/${id}`);
    load(selected);
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">
            Supplies
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Inventory</h1>
        </div>
        <Button onClick={() => setOpen(true)} disabled={!selected} data-testid="add-inventory-button">
          <Plus className="h-4 w-4 mr-2" /> Add item
        </Button>
      </div>

      <div className="mb-6 max-w-xs">
        <Label className="text-xs mb-1.5 block">Property</Label>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger data-testid="inventory-property-select">
            <SelectValue placeholder="Select property" />
          </SelectTrigger>
          <SelectContent>
            {properties.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 text-center">
          <Boxes className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.3} />
          <p className="text-muted-foreground">
            No items tracked here yet. Add supplies like towels, soap, paper, etc.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it) => (
            <Card
              key={it.id}
              className={`p-5 ${it.status === "low" ? "border-[#DD6B20]/40 bg-[#FFF7ED]" : ""}`}
              data-testid={`inventory-item-${it.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {it.name}
                    {it.status === "low" && (
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="h-3 w-3" /> Low
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Refill at {it.threshold} or below
                    {it.note ? ` · "${it.note}"` : ""}
                  </p>
                </div>
                <button onClick={() => remove(it.id)} data-testid={`delete-inventory-${it.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </button>
              </div>
              <div className="flex items-center gap-3 mt-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => updateQty(it, -1)}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="font-display text-2xl font-semibold w-10 text-center">
                  {it.quantity}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => updateQty(it, 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Add inventory item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Item name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Toilet paper"
                data-testid="inventory-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  data-testid="inventory-qty-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Low-stock threshold</Label>
                <Input
                  type="number"
                  value={form.threshold}
                  onChange={(e) => setForm({ ...form, threshold: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={add} disabled={saving} data-testid="save-inventory-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
